import React, {Fragment} from "react";
import shopApi from "../../api/shop";
import {IPageProps} from "../../types/pages";
import {GetServerSideProps, NextPage} from "next";
import {domainUrl} from "../../helper";
import PostPage from "../../components/posts/PostPage";
import {IPostCategory, ITag} from "../../types/posts";
import MainLayout from "../../components/ForCWV/Wrappers/MainLayout";
import {BlogPageTranslations} from "../../services/translationKeys";
import {layoutProps} from "../../services/layoutProps";
import Head from "next/head";
import {useRouter} from "next/router";

interface IProps extends IPageProps {
    postCategories: Array<IPostCategory>,
    postTags: Array<ITag>
}

const PagePosts: NextPage<IProps> = (props) => {
    const {
        selectedLocale,
        domain,
        post_metas: {
            meta_title = "",
            meta_description = "",
            meta_keywords = "",
            share_pic = "",
        },
        dbName,
        posts,
        postCategories,
        postTags,
        breadcrumb,
        translationsData,
        siteName,
        defaultLangCode,
        locales
    } = props
    const router = useRouter();

    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)
    const meta_image = domainUrl(`${dbName}/storage/${domain}/${share_pic}`)

    return (
        <MainLayout {...props} >
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="keywords" content={meta_keywords}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="robots" content="index, follow"/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta name="twitter:image" content={meta_image}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:keywords" content={meta_keywords}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <meta property="og:image" content={meta_image}/>
                    <meta property="og:site_name" content={siteName}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <PostPage
                    dbName={dbName}
                    domain={domain}
                    posts={posts}
                    postCategories={postCategories}
                    postTags={postTags}
                    breadcrumb={breadcrumb}
                    translationsData={translationsData}
                    selectedLocale={selectedLocale}
                />
            </Fragment>
        </MainLayout>
    );

}

export const getServerSideProps: GetServerSideProps = async ({locale, req, query}: any) => {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: BlogPageTranslations,
        ipv4: typedReq.ipv4,
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, Posts: PostsIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const {page = 1, tags: filterTags, category: filterCategory} = query;
    const options = {
        locale: selectedLocale,
        page: page,
        limit: 12,
        filterTags: filterTags?.split(","),
        filterCategory,
        dbName: req.headers["x-forwarded-host"],
        domain: req.defaultDB.db_name
    }

    const posts = await shopApi.getAsyncPosts(options, req.m)
    const blogCategories = await shopApi.getAsyncPostCategories({locale}, req.m)
    const tags = await shopApi.getAsyncTags({locale}, req.m)

    const postsData = {
        data: posts.data,
        total: posts?.meta?.total || 1,
        current_page: page || posts.meta.current_page,
    };

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: PostsIntl, url: ``}
    ]

    wrapperProps.post_metas = {
        ...wrapperProps.post_metas,
        meta_title: wrapperProps.post_metas.meta_title? wrapperProps.post_metas.meta_title : PostsIntl,
    }

    return {
        props: {
            breadcrumb,
            locale: selectedLocale,
            posts: postsData,
            postCategories: JSON.parse(JSON.stringify(blogCategories)),
            postTags: JSON.parse(JSON.stringify(tags)),
            ...wrapperProps,
        },
    };
}

export default PagePosts